import { useRef } from 'react';
import { useStore } from '@nanostores/react';
import { $loggedIn } from '../../skilljar/stores/user';
import { useLessonRegistration } from '../hooks/useLessonRegistration';
import { useTrackSingleLessonResourceView } from '../hooks/useTrackSingleLessonResourceView';
import { useSingleLessonRegistration } from '../hooks/useSingleLessonRegistration';
import { trackViewedResource } from '../../skilljar/stores/viewed-resources';
import { Breadcrumbs } from '@klaviyo-mkt/gatsby-components/molecules/breadcrumbs';
import { LessonBottomNav } from './lesson-bottom-nav';
import { LessonIframes } from './lesson-iframes';
import { LessonTopNav } from './lesson-top-nav';
import { helpers } from './helpers';

import * as s from '../shared/styles/templates/template.module.scss';

import type { ILesson } from '../../graphql/types';

export const Lesson = ({
  lesson,
  path,
}: {
  lesson: ILesson;
  path: string;
}) => {
  const loggedIn = useStore($loggedIn);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  if (lesson && loggedIn) {
    trackViewedResource(lesson.thirdPartyId);
    if (lesson?.course?.thirdPartyId) trackViewedResource(lesson.course.thirdPartyId);
  }

  const {
    isCollectionLesson,
    iframeSrc,
    courseUrl,
    collectionUrl,
    isSingleLessonCourse,
    resourceType,
    pluralizedResourceType,
  } = helpers({ path, lesson });

  // Use custom hooks to handle different concerns
  const { isLoading, error } = useLessonRegistration(lesson, courseUrl, loggedIn, resourceType);
  useTrackSingleLessonResourceView(lesson, isSingleLessonCourse, resourceType);
  useSingleLessonRegistration(lesson, isSingleLessonCourse, loggedIn, resourceType);

  if (!lesson || !lesson.course) {
    return null;
  }

  const unifiedSearchUrl = `/en-us/search${resourceType ? `?select=${encodeURIComponent(resourceType)}` : ''}`;

  const breadcrumbs = [
    // Collection course lesson breadcrumbs
    ...(isCollectionLesson ? [
      {
        link: `/en-us/collections/${lesson.course.collections[0].slug}`,
        text: lesson.course.collections[0].title,
        show: true,
      },
      {
        link: `/en-us/collections/${lesson.course.collections[0].slug}/${lesson.course.slug}`,
        text: lesson.course.title,
        show: lesson.course.slug,
      },
      {
        link: `/en-us/collections/${lesson.course.collections[0].slug}/${lesson.course.slug}/${lesson.id}`,
        text: lesson.title,
        show: lesson.course.slug && lesson.id,
      },
    ] : [
      // Course lesson breadcrumbs
      {
        link: unifiedSearchUrl,
        text: pluralizedResourceType,
        show: isSingleLessonCourse,
      },
      {
        link: `/en-us/courses/${lesson.course.slug}`,
        text: lesson.course.title,
        show: !isSingleLessonCourse && lesson.course.slug,
      },
      {
        link: `/en-us/courses/${lesson.course.slug}/${lesson.id}`,
        text: lesson.title,
        show: lesson.course.slug && lesson.id,
      },
    ]),
  ].filter((b) => Boolean(b.show));

  return (
    <>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      {!isSingleLessonCourse && (
        <LessonTopNav course={lesson.course} coursePath={courseUrl} />
      )}
      <div style={{ height: '100vh', width: '100%' }} id="klaviyo-frame-container">
        {error && (
          <div className={s.loading}>
            There was an error loading this lesson. Try refreshing the page. If you still encounter this error, return
            to the overview page and attempt to register for the course again.
          </div>
        )}
        {isLoading && !error ? (
          <div className={s.loading}><span>Loading...</span></div>
        ) : (
          <LessonIframes
            contentUrl={lesson.contentUrl ?? undefined}
            iframeRef={iframeRef}
            iframeSrc={iframeSrc}
            title={lesson.title}
          />
        )}
      </div>
      <LessonBottomNav coursePath={courseUrl} collectionUrl={collectionUrl} lesson={lesson} />
    </>
  );
};

export default Lesson;
